<template>
  <div style="min-height: var(--app-viewport-height, 100vh); background: linear-gradient(180deg, #EDF3FD 0%, #F8FAFC 100%);">
    <Loader :loading="showLoader" />

    <v-container fluid class="py-4 py-md-6">
      <v-row justify="center">
        <v-col cols="12" lg="11" xl="10">
          <v-sheet color="transparent">
            <v-card
              rounded="xl"
              elevation="4"
              class="px-4 px-md-6 py-4 mb-4"
              style="background: linear-gradient(135deg, #285096 0%, #23A5E6 50%, #59C4FF 100%);"
            >
              <div class="d-flex align-center">
                <v-avatar size="44" class="mr-3" color="white" style="padding: 8px;">
                  <v-img :src="logo" contain />
                </v-avatar>

                <div class="flex-grow-1">
                  <div class="text-h6 font-weight-bold" style="color: #ffffff; line-height: 1.2;">
                    {{ $t('office.workspace') }}
                  </div>
                  <div class="text-caption" style="color: rgba(255,255,255,0.9);">
                    {{ $t('office.smart-building') }}
                  </div>
                </div>

                <div class="d-flex align-center ga-2">
                  <v-btn
                    icon
                    variant="text"
                    color="white"
                    @click="openMapView"
                  >
                    <v-icon size="22">mdi-map-outline</v-icon>
                  </v-btn>

                  <v-btn
                    variant="elevated"
                    color="white"
                    class="text-none font-weight-semibold"
                    style="color: #285096;"
                    @click="openBuildingView"
                  >
                    <v-icon left size="18" color="#285096">mdi-cube-outline</v-icon>
                    <span class="d-none d-sm-inline">{{ $t('office.3d-view') }}</span>
                  </v-btn>
                </div>
              </div>
            </v-card>
          </v-sheet>

          <v-sheet color="transparent" class="mb-4" v-if="selectedBuilding">
            <v-card rounded="xl" elevation="1" class="pa-4" color="white">
              <div class="d-flex flex-column flex-md-row align-md-center justify-space-between ga-3">
                <v-chip
                  size="large"
                  label
                  style="background: linear-gradient(135deg, #285096 0%, #23A5E6 100%); color: white;"
                  class="font-weight-semibold px-4"
                >
                  <v-icon left size="18">mdi-office-building</v-icon>
                  {{ selectedBuilding }}
                </v-chip>

                <v-text-field
                  v-model="search"
                  prepend-inner-icon="mdi-magnify"
                  :label="$t('office.search-spaces')"
                  variant="solo"
                  density="comfortable"
                  rounded="lg"
                  flat
                  hide-details
                  bg-color="#F8FAFC"
                  color="#23A5E6"
                  class="search-field"
                  style="max-width: 400px;"
                ></v-text-field>
              </div>
            </v-card>
          </v-sheet>

          <div class="spaces-grid">
            <v-card
              rounded="xl"
              elevation="3"
              class="utilization-card"
              style="background: linear-gradient(135deg, #5280D1 0%, #285096 100%); position: relative; overflow: hidden;"
              @click="goToBuildingUtilization"
            >
              <div class="card-pattern"></div>
              
              <v-chip
                size="small"
                label
                color="rgba(255, 255, 255, 0.25)"
                class="live-badge"
              >
                <span class="pulse-dot"></span>
                <span class="font-weight-bold text-caption ml-1" style="color: white; letter-spacing: 0.05em;">{{ $t('office.live') }}</span>
              </v-chip>

              <v-card-text class="pa-5" style="position: relative; z-index: 2;">
                <div class="d-flex align-center mb-4">
                  <div class="icon-wrapper mr-3">
                    <v-icon size="32" color="white">mdi-chart-line</v-icon>
                  </div>
                  <div>
                    <h3 class="text-h6 font-weight-bold mb-1" style="color: white; text-shadow: 0 2px 8px rgba(0,0,0,0.3);">
                      {{ $t('office.building-utilization') }}
                    </h3>
                    <p class="text-body-2 mb-0" style="color: rgba(255,255,255,0.95); text-shadow: 0 1px 4px rgba(0,0,0,0.2);">
                      {{ $t('office.real-time-analytics') }}
                    </p>
                  </div>
                </div>

                <div class="stats-row mb-4">
                  <div class="stat-item">
                    <div class="stat-value">85%</div>
                    <div class="stat-label">{{ $t('office.usage-rate') }}</div>
                  </div>
                  <v-divider vertical class="mx-4" style="border-color: rgba(255,255,255,0.3);"></v-divider>
                  <div class="stat-item">
                    <div class="stat-value">142</div>
                    <div class="stat-label">{{ $t('office.active-rooms') }}</div>
                  </div>
                </div>

                <div class="progress-wrapper mb-3">
                  <v-progress-linear
                    model-value="85"
                    height="8"
                    rounded
                    color="white"
                    bg-color="rgba(255,255,255,0.25)"
                  ></v-progress-linear>
                </div>

                <div class="d-flex align-center justify-space-between cta-box">
                  <div class="d-flex align-center">
                    <v-icon size="18" color="#34D399" class="mr-2">mdi-trending-up</v-icon>
                    <span class="text-body-2 font-weight-semibold" style="color: white; text-shadow: 0 1px 4px rgba(0,0,0,0.2);">
                      {{ $t('office.view-detailed-analytics') }}
                    </span>
                  </div>
                  <v-icon size="20" color="white" class="arrow-icon">mdi-arrow-right</v-icon>
                </div>
              </v-card-text>
            </v-card>

            <v-card
              v-for="poiCategory in filteredPoiCategories"
              :key="poiCategory.id"
              rounded="xl"
              elevation="2"
              class="space-card"
              @click="goToPoiCategory(poiCategory)"
            >
              <div class="image-container">
                <v-img
                  :src="poiCategory.image"
                  height="180"
                  cover
                  class="space-image"
                >
                  <template #placeholder>
                    <div class="d-flex align-center justify-center fill-height">
                      <v-progress-circular indeterminate color="#23A5E6"></v-progress-circular>
                    </div>
                  </template>
                </v-img>
                <div class="image-gradient"></div>
              </div>

              <v-card-text class="pa-4">
                <div class="d-flex align-center justify-space-between mb-2">
                  <h4 class="text-h6 font-weight-bold" style="color: #001437; line-height: 1.3;">
                    {{ getCategoryDisplayName(poiCategory) }}
                  </h4>
                  <v-chip
                    size="small"
                    color="#E8F5E9"
                    label
                    class="availability-chip"
                  >
                    <span class="availability-dot"></span>
                    <span class="text-caption font-weight-bold ml-1" style="color: #2E7D32;">
                      {{ poiCategory.pois?.length || 0 }} {{ $t('office.available-count') }}
                    </span>
                  </v-chip>
                </div>

                <div class="d-flex align-center justify-space-between mt-3">
                  <div class="d-flex align-center">
                    <v-icon size="16" color="#9AA6B1" class="mr-1">mdi-door-open</v-icon>
                    <span class="text-body-2" style="color: #4E5965;">
                      {{ poiCategory.pois?.length || 0 }} {{ $t('office.rooms') }}
                    </span>
                  </div>
                  <div class="explore-link">
                    <span class="text-body-2 font-weight-semibold mr-1" style="color: #23A5E6;">
                      {{ $t('office.explore') }}
                    </span>
                    <v-icon size="16" color="#23A5E6" class="explore-arrow">mdi-arrow-right</v-icon>
                  </div>
                </div>
              </v-card-text>

              <div class="hover-overlay"></div>
            </v-card>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <v-dialog v-model="showDialog" persistent max-width="500">
      <v-card rounded="xl" elevation="8">
        <div class="dialog-header pa-5" style="background: linear-gradient(135deg, #285096 0%, #23A5E6 100%);">
          <div class="d-flex align-center ga-3">
            <v-avatar size="48" color="rgba(255,255,255,0.2)">
              <v-icon color="white" size="28">mdi-office-building</v-icon>
            </v-avatar>
            <h3 class="text-h6 font-weight-bold" style="color: white;">
              {{ $t('office.select-building') }}
            </h3>
          </div>
        </div>

        <v-card-text class="pa-5">
          <v-select
            :items="buildings"
            item-value="name"
            item-title="name"
            :label="$t('office.choose-building')"
            v-model="tempSelectedBuilding"
            return-object
            variant="outlined"
            rounded="lg"
            color="#23A5E6"
            class="mt-2"
          ></v-select>
        </v-card-text>

        <v-card-actions class="pa-5 pt-0">
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            color="#4E5965"
            rounded="lg"
            class="text-none font-weight-semibold px-4"
            @click="cancelSelection"
          >
            {{ $t('office.cancel') }}
          </v-btn>
          <v-btn
            variant="elevated"
            rounded="lg"
            class="text-none font-weight-semibold px-5"
            style="background: linear-gradient(135deg, #285096 0%, #23A5E6 100%); color: white;"
            elevation="2"
            @click="confirmSelection"
          >
            {{ $t('office.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <MapView ref="mapView" />
    <OutsideBuildingView ref="buildingView" />
  </div>
</template>

<script>
  import { defineComponent, ref, onMounted, computed, onBeforeUnmount } from 'vue';
  import { useRouter } from 'vue-router';
  import { useI18n } from 'vue-i18n';
  import { debounce, flatten, some, find, sortBy } from 'lodash';
  import { Preferences } from '@capacitor/preferences';
  import { Device } from '@capacitor/device';
  import {
    getStoredItem,
    setStoredItem,
    isWebPlatform,
    setCookie,
    getCookie
  } from '@/services/utils.js';
  import {
    getPrimaryColor,
    getToolbarLogoImage,
    getToolBarCustomerName
  } from '@/assets/Branding/branding.js';
  import {
    getBuildings,
    getPoiCategoriesByBuildingId,
    getAvailablePois
  } from '@/controllers/BaseController';
  import Loader from '@/components/general/Loader.vue';
  import MapView from '@/components/general/MapView.vue';
  import OutsideBuildingView from '@/components/general/ChildComponents/OutsideBuildingView.vue';
  import moment from 'moment';

  export default defineComponent({
    name: 'BuildingView',
    components: {
      Loader,
      MapView,
      OutsideBuildingView,
    },
    setup() {
      const { t, locale } = useI18n();
      const showDialog = ref(false);
      const search = ref('');
      const logo = ref('');
      const primaryColor = ref('');
      const customerName = ref('');
      const showLoader = ref(true);
      const buildings = ref([]);
      const selectedBuilding = ref(null);
      const tempSelectedBuilding = ref(null);
      const poiCategories = ref([]);
      const isMobileView = ref(window.innerWidth <= 960);
      const mapView = ref(null);
      const buildingView = ref(null);
      const router = useRouter();
      const currentLanguage = ref('en');

      const backupImageMap = {
        'meeting room': require('@/assets/PoiImages/backup_poi_images/meeting_room.png'),
        'lab': require('@/assets/PoiImages/backup_poi_images/lab_room.png'),
        'restrooms': require('@/assets/PoiImages/backup_poi_images/rest_room.png'),
        'printer': require('@/assets/PoiImages/backup_poi_images/printer_room.png'),
        'studio': require('@/assets/PoiImages/backup_poi_images/studio.png'),
        'lager': require('@/assets/PoiImages/backup_poi_images/lager_room.png'),
        'food and beverages': require('@/assets/PoiImages/backup_poi_images/kitchen_room.png'),
        'event space': require('@/assets/PoiImages/backup_poi_images/event_space.png'),
        'showers': require('@/assets/PoiImages/backup_poi_images/shower.png'),
      };

      const handleResize = () => {
        isMobileView.value = window.innerWidth <= 960;
      };

      const goToBuildingUtilization = () => {
        router.push({ name: 'BuildingUtilization' });
      };

      const getCategoryDisplayName = (category) => {
        if (currentLanguage.value === 'de' && category.name_de) {
          return category.name_de;
        }
        return category.name;
      };

      const getBackupImage = (categoryName) => {
        const normalizedName = categoryName.toLowerCase().trim();
        
        if (backupImageMap[normalizedName]) {
          return backupImageMap[normalizedName];
        }
        
        for (const [key, image] of Object.entries(backupImageMap)) {
          if (normalizedName.includes(key) || key.includes(normalizedName)) {
            return image;
          }
        }
        
        return require('@/assets/CategoryImages/teamzone_iq.jpg');
      };

      const filteredPoiCategories = computed(() => {
        const nonBookableCategories = ['restroom', 'restrooms', 'toilets', 'wc', 'toilet'];
        
        let filtered = poiCategories.value.filter(cat => {
          const categoryName = cat.name.toLowerCase();
          return !nonBookableCategories.some(excluded => categoryName.includes(excluded));
        });
        
        if (!search.value) return filtered;
        const term = search.value.toLowerCase();
        return filtered.filter(cat => {
          const displayName = getCategoryDisplayName(cat).toLowerCase();
          return displayName.includes(term);
        });
      });

      const loadLanguageFromCookie = async () => {
        const savedLanguage = await getCookie('language');
        if (savedLanguage) {
          currentLanguage.value = savedLanguage;
          locale.value = savedLanguage;
        }
      };

      async function loadData() {
        try {
          window.addEventListener('resize', handleResize);

          await loadLanguageFromCookie();

          const [toolbarLogo, customer, apiBuildings] = await Promise.all([
            getToolbarLogoImage(),
            getToolBarCustomerName(),
            getBuildings()
          ]);

          const piaBuilding = apiBuildings.find(b => b.id === 'e1e1c326-f8e5-472b-9ea7-673c59550d46')
          const allowedBuildings = piaBuilding ? [piaBuilding] : apiBuildings.filter(b => b.name === 'Pia Building')

          logo.value = toolbarLogo;
          customerName.value = customer;
          buildings.value = allowedBuildings.length ? allowedBuildings : apiBuildings;
          primaryColor.value = await getPrimaryColor();

          const storedBuilding = await getStoredItem('selectedBuilding');
          selectedBuilding.value = storedBuilding || buildings.value[0]?.name || 'Pia Building';

          await setStoredItem('selectedBuilding', selectedBuilding.value);

          const selectedBuildingId = buildings.value.find(b => b.name === selectedBuilding.value)?.id;
          if (!selectedBuildingId) return;

          let categories = await getPoiCategoriesByBuildingId(selectedBuildingId);

          for (const category of categories) {
            category.image = getBackupImage(category.name);
            category.displayName = getCategoryDisplayName(category);
          }

          poiCategories.value = sortBy(categories, ['displayName']);
        } catch (error) {
          console.error('[BuildingView] Failed to load initial data:', error);
        } finally {
          showLoader.value = false;
        }
      }

      function confirmSelection() {
        selectedBuilding.value = 'Pia Building';
        setStoredItem('selectedBuilding', 'Pia Building');
        showDialog.value = false;
        window.location.reload();
      }

      function cancelSelection() {
        tempSelectedBuilding.value = selectedBuilding.value;
        showDialog.value = false;
      }

      function goToPoiCategory(poiCategory) {
        const category = { ...poiCategory };
        const building = buildings.value.find(b => b.name === selectedBuilding.value);
        if (!building) return;

        router.push({
          name: 'PoisListView',
          params: {
            building_id: building.id,
            category_id: category.id,
          },
        });
      }

      function openDialog() {
        tempSelectedBuilding.value = selectedBuilding.value;
        showDialog.value = true;
      }

      function openMapView() {
        const building = buildings.value.find(b => b.name === selectedBuilding.value);
        if (!building) return;

        const POIS = flatten(poiCategories.value.map(cat => cat.pois || []));
        mapView.value?.openMap({ ...building }, POIS, [...poiCategories.value]);
      }

      function openBuildingView() {
        buildingView.value?.openBuilding();
      }

      function closeBuildingView() {
        buildingView.value?.closeBuilding();
      }

      onMounted(async () => {
        await loadData();
        const startDate = moment.utc('2025-08-04T14:00:00')
        const endDate = moment.utc('2025-08-04T15:00:00')
        const pois = await getAvailablePois(startDate, endDate);
      });
      
      onBeforeUnmount(() => window.removeEventListener('resize', handleResize));

      return {
        showDialog,
        search,
        logo,
        customerName,
        buildings,
        selectedBuilding,
        tempSelectedBuilding,
        showLoader,
        poiCategories,
        filteredPoiCategories,
        confirmSelection,
        goToPoiCategory,
        cancelSelection,
        openDialog,
        openMapView,
        openBuildingView,
        closeBuildingView,
        mapView,
        buildingView,
        primaryColor,
        isMobileView,
        handleResize,
        goToBuildingUtilization,
        getCategoryDisplayName
      };
    },
  });
</script>

<style scoped>
.spaces-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;
}

.utilization-card {
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-height: 320px;
}

.utilization-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(82, 128, 209, 0.4) !important;
}

.card-pattern {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 1;
  pointer-events: none;
}

.live-badge {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.pulse-dot {
  width: 8px;
  height: 8px;
  background: white;
  border-radius: 50%;
  display: inline-block;
  animation: pulse 2s infinite;
  box-shadow: 0 0 8px rgba(255, 255, 255, 0.8);
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.1); }
}

.icon-wrapper {
  width: 56px;
  height: 56px;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.stats-row {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 14px;
  padding: 16px;
  border: 1px solid rgba(255, 255, 255, 0.25);
}

.stat-item {
  text-align: center;
  flex: 1;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: 800;
  color: white;
  line-height: 1;
  margin-bottom: 4px;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.25);
}

.stat-label {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
}

.progress-wrapper :deep(.v-progress-linear) {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.cta-box {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 12px 16px;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.25);
}

.utilization-card:hover .cta-box {
  background: rgba(255, 255, 255, 0.25);
}

.cta-text {
  font-size: 0.875rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
}

.arrow-icon {
  transition: transform 0.3s ease;
}

.utilization-card:hover .arrow-icon {
  transform: translateX(4px);
}

.space-card {
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  background: white;
}

.space-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12) !important;
}

.image-container {
  position: relative;
  overflow: hidden;
}

.space-image {
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.space-card:hover .space-image {
  transform: scale(1.08);
}

.image-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.15) 100%);
  transition: background 0.3s ease;
}

.space-card:hover .image-gradient {
  background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.25) 100%);
}

.availability-chip {
  flex-shrink: 0;
}

.availability-dot {
  width: 6px;
  height: 6px;
  background: #2E7D32;
  border-radius: 50%;
  display: inline-block;
  animation: pulse 2s infinite;
}

.explore-link {
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
}

.space-card:hover .explore-link {
  transform: translateX(4px);
}

.explore-arrow {
  transition: transform 0.3s ease;
}

.space-card:hover .explore-arrow {
  transform: translateX(2px);
}

.hover-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(35, 165, 230, 0.05) 0%, transparent 50%);
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.space-card:hover .hover-overlay {
  opacity: 1;
}

.search-field :deep(.v-field) {
  border: 2px solid #E8EEF5;
  transition: all 0.2s ease;
}

.search-field :deep(.v-field:hover) {
  background: white !important;
  border-color: #23A5E6;
}

.search-field :deep(.v-field--focused) {
  background: white !important;
  border-color: #285096;
  box-shadow: 0 0 0 3px rgba(35, 165, 230, 0.1);
}

@media (max-width: 1200px) {
  .spaces-grid {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 20px;
  }
}

@media (max-width: 960px) {
  .spaces-grid {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 18px;
  }

  .utilization-card {
    min-height: 300px;
  }
}

@media (max-width: 768px) {
  .spaces-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .utilization-card {
    min-height: 280px;
  }

  .stat-value {
    font-size: 1.5rem;
  }

  .stat-label {
    font-size: 0.7rem;
  }
}

@media (max-width: 600px) {
  .utilization-card {
    min-height: 260px;
  }

  .icon-wrapper {
    width: 48px;
    height: 48px;
  }

  .stats-row {
    padding: 12px;
  }

  .stat-value {
    font-size: 1.375rem;
  }
}

@media (max-width: 450px) {
  .utilization-card {
    min-height: 240px;
  }

  .icon-wrapper {
    width: 44px;
    height: 44px;
  }

  .stat-value {
    font-size: 1.25rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .pulse-dot,
  .availability-dot,
  .space-image,
  .arrow-icon,
  .explore-arrow {
    animation: none;
    transition: none;
  }
}
</style>
